"use client";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Video, Loader2, CheckCircle2, AlertTriangle, Upload, RotateCcw, Sparkles, Square,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getUploadConfig, uploadVideoDirect, uploadVideoToServer } from "./videoUpload";

/**
 * Token-authenticated public page for the QR / emailed-request handoff:
 *   /video-resume/mobile/:token   (no login)
 *
 * Capability-adaptive capture — one page for every device:
 *   • Phone      → native camera (input capture) + upload fallback
 *   • Desktop    → in-browser webcam recorder (getUserMedia + MediaRecorder)
 *                  + upload fallback
 *   • No camera / http / denied → upload only, with a clear note
 *
 * Every path produces the same blob → same preview → same async upload
 * (progress bar) → "Reviewing your video…" poll → review. Phone behaviour is
 * unchanged; desktop simply gains real recording instead of upload-only.
 */
function MobileVideoUpload() {
  const { token } = useParams();
  const captureInputRef = useRef(null); // phone native camera (capture="user")
  const uploadInputRef = useRef(null);  // plain file picker (all devices)
  const liveVideoRef = useRef(null);    // live webcam preview while recording
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const pollRef = useRef({ active: false });

  // Decide capture method from CAPABILITIES, not user-agent strings.
  const [caps] = useState(() => {
    let isPhone = false;
    let canWebcam = false;
    try {
      isPhone = (navigator.maxTouchPoints || 0) > 0 &&
                !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      canWebcam = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
                  typeof window.MediaRecorder !== "undefined" &&
                  window.isSecureContext === true;
    } catch { /* leave defaults */ }
    return { isPhone, canWebcam };
  });

  const [tokenState, setTokenState] = useState("checking"); // checking | valid | invalid
  const [emailHint, setEmailHint] = useState("");
  const [file, setFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [phase, setPhase] = useState("pick"); // pick | recording | preview | uploading | reviewing | done | error
  const [recSeconds, setRecSeconds] = useState(0);
  const [uploadPct, setUploadPct] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [cfg, setCfg] = useState({ directUpload: false, maxDirectBytes: 500 * 1024 * 1024, maxServerBytes: 90 * 1024 * 1024 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await fetch(`/api/video-resume/mobile/${token}/status`);
        const json = await resp.json();
        if (!alive) return;
        if (json.valid) {
          setTokenState("valid");
          setEmailHint(json.email_hint || "");
        } else {
          setTokenState("invalid");
        }
      } catch {
        if (alive) setTokenState("invalid");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Release camera + timers + poll on unmount.
  useEffect(() => () => {
    pollRef.current.active = false;
    stopStream();
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  }, []);

  // Which upload path (direct-to-S3 vs server) + the size cap to enforce.
  useEffect(() => { getUploadConfig().then(setCfg).catch(() => {}); }, []);

  // Attach the live stream to the <video> once the recording view is mounted.
  useEffect(() => {
    if (phase === "recording" && liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
  }, [phase]);

  const stopStream = () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${s % 60 < 10 ? "0" : ""}${s % 60}`;

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const maxBytes = cfg.directUpload ? cfg.maxDirectBytes : cfg.maxServerBytes;
    if (f.size > maxBytes) {
      setErrorMsg(`That video is too large (max ${Math.round(maxBytes / 1024 / 1024)} MB) — record a shorter clip or lower your camera's video quality in Settings.`);
      setPhase("error");
      return;
    }
    setErrorMsg("");
    setFile(f);
    setPreviewURL(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setPhase("preview");
  };

  // ── Desktop in-browser webcam recording ──────────────────────────────────
  const pickMime = () => {
    const cands = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const m of cands) {
      try { if (window.MediaRecorder?.isTypeSupported?.(m)) return m; } catch { /* noop */ }
    }
    return "";
  };

  const startWebcamRecording = async () => {
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;
      setRecSeconds(0);
      setPhase("recording");

      const mime = pickMime();
      const opts = { videoBitsPerSecond: 1500000 }; // ~1.5 Mbps keeps files small + AI-reviewable
      if (mime) opts.mimeType = mime;
      const rec = new MediaRecorder(stream, opts);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = mime || "video/webm";
        const blob = new Blob(chunksRef.current, { type });
        stopStream();
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        if (!blob || blob.size === 0) {
          setErrorMsg("Recording failed — please try again.");
          setPhase("error");
          return;
        }
        if (blob.size > 90 * 1024 * 1024) {
          setErrorMsg("That recording is too large — please record a shorter clip.");
          setPhase("error");
          return;
        }
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const f = new File([blob], `webcam-recording.${ext}`, { type });
        setFile(f);
        setPreviewURL(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
        setPhase("preview");
      };

      rec.start();
      recTimerRef.current = setInterval(() => {
        setRecSeconds(s => {
          const n = s + 1;
          if (n >= 120 && recorderRef.current && recorderRef.current.state === "recording") {
            try { recorderRef.current.stop(); } catch { /* noop */ } // hard cap at 2 min
          }
          return n;
        });
      }, 1000);
    } catch {
      stopStream();
      setErrorMsg("Couldn't access your camera. Allow camera access in your browser, or upload a video file instead.");
      setPhase("pick");
    }
  };

  const stopWebcamRecording = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      try { recorderRef.current.stop(); } catch { /* noop */ } // fires onstop → preview
    }
  };

  // ── Poll the async AI review ──────────────────────────────────────────────
  const pollForReview = (prevAt) => {
    pollRef.current.active = true;
    let tries = 0;
    const maxTries = 24; // ~60s at 2.5s
    const tick = async () => {
      if (!pollRef.current.active) return;
      tries += 1;
      try {
        const resp = await fetch(`/api/video-resume/mobile/${token}/analysis`);
        const json = await resp.json();
        const a = json && json.analysis;
        if (a && a.analyzed_at && a.analyzed_at !== prevAt) {
          if (!pollRef.current.active) return;
          setAnalysis(a);
          setPhase("done");
          return;
        }
      } catch { /* transient — keep polling */ }
      if (tries >= maxTries) {
        if (!pollRef.current.active) return;
        setReviewNote("Your video is saved. The AI review is taking a little longer and will appear on your profile shortly.");
        setPhase("done");
        return;
      }
      setTimeout(tick, 2500);
    };
    setTimeout(tick, 2500);
  };

  const upload = async () => {
    if (!file) return;
    setPhase("uploading");
    setUploadPct(0);
    setErrorMsg("");
    setAnalysis(null);
    setReviewNote("");

    const durationSeconds = recSeconds || null;
    try {
      let res;
      if (cfg.directUpload) {
        res = await uploadVideoDirect({
          endpoints: {
            presign: `/api/video-resume/mobile/${token}/presign`,
            finalize: `/api/video-resume/mobile/${token}/finalize`,
          },
          file, durationSeconds, onProgress: setUploadPct,
        });
      } else {
        res = await uploadVideoToServer({
          url: `/api/video-resume/mobile/${token}/upload`,
          file, durationSeconds, onProgress: setUploadPct,
        });
      }
      if (res.review_pending) {
        setPhase("reviewing");
        pollForReview(res.prev_analyzed_at || null);
      } else {
        setReviewNote("Your video is saved. It was a bit long for an instant AI review, but recruiters can watch it right away.");
        setPhase("done");
      }
    } catch (e) {
      if (e && e.status === 410) {
        setTokenState("invalid");
        setErrorMsg("This link has expired.");
        setPhase("error");
        return;
      }
      setErrorMsg((e && e.message) || "Upload failed — check your connection and try again.");
      setPhase("preview");
    }
  };

  // Back to the chooser — works for every capture method.
  const retake = () => {
    pollRef.current.active = false;
    stopStream();
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    setFile(null);
    setAnalysis(null);
    setReviewNote("");
    setUploadPct(0);
    setRecSeconds(0);
    setErrorMsg("");
    setPreviewURL(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPhase("pick");
  };

  return (
    <div className={cn("min-h-screen bg-background flex flex-col items-center px-4 py-8")}>
      <div className={cn("w-full max-w-md space-y-4")}>
        <div className={cn("text-center space-y-1")}>
          <h1 className={cn("text-xl font-bold text-foreground flex items-center justify-center gap-2")}>
            <Video className="w-5 h-5 text-primary" />
            Video Resume
          </h1>
          {tokenState === "valid" && emailHint && (
            <p className={cn("text-xs text-muted-foreground")}>Uploading for {emailHint}</p>
          )}
        </div>

        {tokenState === "checking" && (
          <Card><CardContent className={cn("p-8 flex justify-center")}>
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </CardContent></Card>
        )}

        {tokenState === "invalid" && (
          <Card className={cn("border-destructive/40")}>
            <CardContent className={cn("p-6 text-center space-y-2")}>
              <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
              <p className={cn("font-semibold text-foreground")}>This link has expired</p>
              <p className={cn("text-sm text-muted-foreground")}>
                If you scanned a QR code, generate a fresh one on your computer.
                If this came from an email, ask the employer to send a new
                request — or log in at aievaluator.in and record from your profile.
              </p>
            </CardContent>
          </Card>
        )}

        {tokenState === "valid" && (
          <>
            {/* Hidden file inputs: capture=phone camera, plain=file picker */}
            <input ref={captureInputRef} type="file" accept="video/*" capture="user"
              onChange={onPick} style={{ display: "none" }} />
            <input ref={uploadInputRef} type="file" accept="video/*"
              onChange={onPick} style={{ display: "none" }} />

            {phase === "pick" && (
              <Card>
                <CardContent className={cn("p-6 space-y-4")}>
                  <ul className={cn("text-sm text-muted-foreground space-y-1.5")}>
                    <li>✓ 30–60 seconds is ideal (10s minimum)</li>
                    <li>✓ Say your name, role, and strongest skills</li>
                    <li>✓ One project you&apos;re proud of</li>
                    <li>✓ What role you&apos;re looking for</li>
                  </ul>

                  {errorMsg && <p className={cn("text-sm text-destructive")}>{errorMsg}</p>}

                  {/* Primary record option — adaptive per device */}
                  {caps.isPhone ? (
                    <Button size="lg" className={cn("w-full gap-2")} onClick={() => captureInputRef.current?.click()}>
                      <Video className="w-5 h-5" /> Record with camera
                    </Button>
                  ) : caps.canWebcam ? (
                    <Button size="lg" className={cn("w-full gap-2")} onClick={startWebcamRecording}>
                      <Video className="w-5 h-5" /> Record with your webcam
                    </Button>
                  ) : null}

                  {/* Upload — always available on every device */}
                  <Button
                    size="lg"
                    variant={caps.isPhone || caps.canWebcam ? "outline" : "default"}
                    className={cn("w-full gap-2")}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    <Upload className="w-5 h-5" /> Upload a video file
                  </Button>

                  {!caps.isPhone && !caps.canWebcam && (
                    <p className={cn("text-xs text-muted-foreground text-center")}>
                      To record with a camera, open this link on your phone, or use a secure (https) page with camera access.
                    </p>
                  )}
                  {!caps.isPhone && caps.canWebcam && (
                    <p className={cn("text-xs text-muted-foreground text-center")}>
                      Prefer your phone? Open this link there to use the camera.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {phase === "recording" && (
              <Card>
                <CardContent className={cn("p-4 space-y-3")}>
                  <div className={cn("relative")}>
                    <video
                      ref={liveVideoRef}
                      autoPlay
                      muted
                      playsInline
                      className={cn("w-full rounded-lg bg-black max-h-[50vh]")}
                    />
                    <div className={cn("absolute top-2 left-2 flex items-center gap-1.5 bg-black/50 px-2 py-1 rounded-full")}>
                      <span className={cn("w-2 h-2 rounded-full bg-red-500 animate-pulse")} />
                      <span className={cn("text-xs text-white font-medium tabular-nums")}>{formatTime(recSeconds)}</span>
                    </div>
                  </div>
                  <Button
                    onClick={stopWebcamRecording}
                    disabled={recSeconds < 10}
                    variant="destructive"
                    className={cn("w-full gap-2")}
                  >
                    <Square className="w-4 h-4" />
                    {recSeconds < 10 ? `Keep going… ${10 - recSeconds}s more` : "Stop & review"}
                  </Button>
                  <p className={cn("text-xs text-muted-foreground text-center")}>
                    Recording stops automatically at 2 minutes.
                  </p>
                </CardContent>
              </Card>
            )}

            {(phase === "preview" || phase === "uploading") && previewURL && (
              <Card>
                <CardContent className={cn("p-4 space-y-4")}>
                  <video
                    src={previewURL}
                    controls
                    playsInline
                    className={cn("w-full rounded-lg bg-black max-h-[50vh]")}
                  />

                  {phase === "uploading" && (
                    <div className={cn("space-y-1.5")}>
                      <div className={cn("h-2 w-full rounded-full bg-muted overflow-hidden")}>
                        <div className={cn("h-full bg-primary transition-all duration-200")} style={{ width: `${uploadPct}%` }} />
                      </div>
                      <p className={cn("text-xs text-muted-foreground text-center")}>
                        {uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Finishing up…"}
                      </p>
                    </div>
                  )}

                  {errorMsg && phase !== "uploading" && (
                    <p className={cn("text-sm text-destructive")}>{errorMsg}</p>
                  )}

                  <div className={cn("grid grid-cols-2 gap-3")}>
                    <Button variant="outline" onClick={retake} disabled={phase === "uploading"} className={cn("gap-2")}>
                      <RotateCcw className="w-4 h-4" />
                      Retake
                    </Button>
                    <Button onClick={upload} disabled={phase === "uploading"} className={cn("gap-2")}>
                      {phase === "uploading"
                        ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading…</>
                        : <><Upload className="w-4 h-4" />Use this video</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {phase === "reviewing" && (
              <Card className={cn("border-primary/30")}>
                <CardContent className={cn("p-6 text-center space-y-3")}>
                  <CheckCircle2 className="w-9 h-9 text-success mx-auto" />
                  <p className={cn("font-semibold text-foreground")}>Uploaded ✓</p>
                  <div className={cn("flex items-center justify-center gap-2 text-sm font-medium text-primary")}>
                    <Sparkles className="w-4 h-4" />
                    <span>Reviewing your video…</span>
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                  <div className={cn("h-1.5 w-full rounded-full bg-muted overflow-hidden")}>
                    <div className={cn("h-full w-1/3 bg-primary/70 rounded-full animate-pulse")} />
                  </div>
                  <p className={cn("text-xs text-muted-foreground")}>
                    This can take up to a minute. You can close this page — your
                    video is saved and your recruiter will see it either way.
                  </p>
                </CardContent>
              </Card>
            )}

            {phase === "error" && errorMsg && tokenState === "valid" && (
              <Card className={cn("border-destructive/40")}>
                <CardContent className={cn("p-6 text-center space-y-3")}>
                  <AlertTriangle className="w-8 h-8 text-destructive mx-auto" />
                  <p className={cn("text-sm text-muted-foreground")}>{errorMsg}</p>
                  <Button variant="outline" onClick={retake}>Try again</Button>
                </CardContent>
              </Card>
            )}

            {phase === "done" && (
              <Card className={cn("border-success/40")}>
                <CardContent className={cn("p-6 space-y-4")}>
                  <div className={cn("text-center space-y-2")}>
                    <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
                    <p className={cn("font-semibold text-foreground")}>Video uploaded!</p>
                    <p className={cn("text-sm text-muted-foreground")}>
                      {reviewNote || "You're all set — you can close this page."}
                    </p>
                  </div>

                  {analysis && analysis.score != null && (
                    <div className={cn("rounded-lg border border-border bg-muted/30 p-4 space-y-2")}>
                      <div className={cn("flex items-center justify-between")}>
                        <span className={cn("text-sm font-semibold")}>AI review</span>
                        <Badge variant={analysis.verdict === "ready" ? "default" : "secondary"}>
                          {analysis.score}/100
                        </Badge>
                      </div>
                      {(analysis.improvements || []).map((tip, i) => (
                        <p key={i} className={cn("text-xs text-muted-foreground")}>→ {tip}</p>
                      ))}
                      {analysis.verdict !== "ready" && (
                        <Button variant="outline" size="sm" onClick={retake} className={cn("w-full gap-2 mt-2")}>
                          <RotateCcw className="w-4 h-4" />
                          Record a better take
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MobileVideoUpload;
